import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, Outlet } from 'react-router-dom';
import { AdminHeader } from './AdminHeader';
import { AdminSidebar } from './AdminSidebar';
import { useAdminSidebar } from '@/contexts/AdminSidebarContext';
import { AIJobStatusProvider } from '@/components/admin/ai-generation';

/**
 * AdminLayout - Persistentes Layout für den Admin-Bereich
 *
 * Verwendet react-router's Outlet für nested routes.
 * Das Layout (Header, Sidebar) bleibt beim Seitenwechsel erhalten,
 * nur der Content-Bereich wird ausgetauscht.
 */
export function AdminLayout() {
  const { user, loading } = useAuth();
  const { isOpen, close } = useAdminSidebar();

  const closeSidebar = () => close();

  // Adjust sidebar height when footer is visible
  // NOTE: This hook must be called before any conditional returns (Rules of Hooks)
  React.useEffect(() => {
    // Skip effect during loading or if user is not admin
    if (loading || !user || (user.role !== 'admin' && user.role !== 'root_admin' && user.role !== 'demo_admin')) {
      return;
    }

    const footer = document.querySelector('footer');
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const sidebar = document.querySelector('.admin-sidebar-fixed');
        if (!sidebar) return;

        if (entry.isIntersecting) {
          // Footer is visible - reduce sidebar height
          const footerHeight = footer.offsetHeight;
          (sidebar as HTMLElement).style.bottom = `${footerHeight}px`;
        } else {
          // Footer not visible - sidebar goes to bottom
          (sidebar as HTMLElement).style.bottom = '0px';
        }
      },
      { threshold: 0 }
    );

    observer.observe(footer);
    return () => observer.disconnect();
  }, [loading, user]);

  // Wait for auth initialization before making redirect decisions
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
      </div>
    );
  }

  // Redirect non-admins (allow admin, root_admin and demo_admin)
  if (!user || (user.role !== 'admin' && user.role !== 'root_admin' && user.role !== 'demo_admin')) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <AIJobStatusProvider>
      {/* Desktop & Mobile: Normal scroll behavior like user pages */}
      <div className="admin-layout-root design-page-gradient min-h-screen">
        {/* Admin Header - fixed, unter User-Nav (top-16), nur Desktop */}
        <AdminHeader />

        {/* Sidebar - FIXED on Desktop (not in flex), Off-Canvas on Mobile */}
        <AdminSidebar isOpen={isOpen} onClose={closeSidebar} />

        {/* Content-Container: margin-left for fixed sidebar, pt-28 accounts for fixed admin header (80px) + spacing */}
        <main className="pt-token-lg pb-24 px-token-md md:pt-28 md:pb-24 md:px-token-lg md:ml-72 lg:ml-80">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </AIJobStatusProvider>
  );
}